// form
import { useFormContext, Controller } from 'react-hook-form';
// @mui
import {
  Checkbox,
  FormLabel,
  FormGroup,
  FormControl,
  FormHelperText,
  FormControlLabel,
  FormControlLabelProps,
} from '@mui/material';

// ----------------------------------------------------------------------

interface RHFCheckboxProps extends Omit<FormControlLabelProps, 'control'> {
  name: string;
  helperText?: React.ReactNode;
}

export function RHFCheckbox({ name, helperText, ...other }: RHFCheckboxProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <div>
          <FormControlLabel control={<Checkbox {...field} checked={field.value} />} {...other} />

          {(!!error || helperText) && (
            <FormHelperText error={!!error}>{error ? error?.message : helperText}</FormHelperText>
          )}
        </div>
      )}
    />
  );
}

// ----------------------------------------------------------------------

type RHFMultiCheckboxOption = { label: string; value: any; parentValue?: any };
type RHFMultiCheckboxGroup = { label: string; options: RHFMultiCheckboxOption[] };

interface RHFMultiCheckboxProps extends Omit<FormControlLabelProps, 'control' | 'label'> {
  name: string;
  options: RHFMultiCheckboxOption[] | RHFMultiCheckboxGroup[];
  row?: boolean;
  label?: string;
  spacing?: number;
  helperText?: React.ReactNode;
}

export function RHFMultiCheckbox({
  row,
  name,
  label,
  options,
  spacing,
  helperText,
  ...other
}: RHFMultiCheckboxProps) {
  const { control } = useFormContext();

  const spacingValue = (spacing ?? 0) / 2;

  const getSelected = (
    selectedItems: string[],
    option: RHFMultiCheckboxOption,
    allOptions: RHFMultiCheckboxOption[]
  ) => {
    const isSelected = selectedItems.includes(option.value);
    if (isSelected) {
      const next = selectedItems.filter((value) => value !== option.value);
      const isParent = allOptions.some((item) => item.parentValue === option.value);
      if (!isParent) {
        return next;
      }
      return next.filter(
        (value) => !allOptions.some((item) => item.parentValue === option.value && item.value === value)
      );
    }

    const next = [...selectedItems, option.value];
    if (option.parentValue && !next.includes(option.parentValue)) {
      next.push(option.parentValue);
    }

    return next;
  };

  const isGroupedOptions = (
    list: RHFMultiCheckboxOption[] | RHFMultiCheckboxGroup[]
  ): list is RHFMultiCheckboxGroup[] =>
    Array.isArray(list) && list.length > 0 && 'options' in list[0];

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <FormControl component="fieldset">
          {label && (
            <FormLabel
              component="legend"
              sx={{
                typography: 'body2',
                color: 'text.secondary',
                '&.Mui-focused': { color: 'text.secondary' },
              }}
            >
              {label}
            </FormLabel>
          )}

          {isGroupedOptions(options) ? (
            <div style={{ display: 'grid', gap: 16, marginTop: 8 }}>
              {options.map((group) => (
                <div key={group.label}>
                  <FormLabel
                    component="legend"
                    sx={{
                      typography: 'subtitle2',
                      color: 'text.secondary',
                      '&.Mui-focused': { color: 'text.secondary' },
                    }}
                  >
                    {group.label}
                  </FormLabel>
                  <FormGroup
                    sx={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      '& .MuiFormControlLabel-root': {
                        mr: spacingValue || 1,
                        mb: spacingValue || 1,
                      },
                    }}
                  >
                    {group.options.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={field.value.includes(option.value)}
                        onChange={() =>
                          field.onChange(getSelected(field.value, option, group.options))
                        }
                      />
                    }
                    label={option.label}
                        {...other}
                      />
                    ))}
                  </FormGroup>
                </div>
              ))}
            </div>
          ) : (
            <FormGroup
              sx={{
                ...(row && {
                  flexDirection: 'row',
                }),
                '& .MuiFormControlLabel-root': {
                  '&:not(:last-of-type)': {
                    mb: spacing || 0,
                  },
                  ...(row && {
                    mr: 0,
                    '&:not(:last-of-type)': {
                      mr: spacing || 2,
                    },
                  }),
                },
              }}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={field.value.includes(option.value)}
                    onChange={() =>
                      field.onChange(
                        getSelected(field.value, option, options as RHFMultiCheckboxOption[])
                      )
                    }
                  />
                }
                  label={option.label}
                  {...other}
                />
              ))}
            </FormGroup>
          )}

          {(!!error || helperText) && (
            <FormHelperText error={!!error} sx={{ mx: 0 }}>
              {error ? error?.message : helperText}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
}
